import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import TextField from '../../lib/text-field/TextField';
import TextArea from '../../lib/text-area/TextArea';
import {
	ChangeSet,
	ProjectState,
	StateMutationAction,
} from '@elyx-code/project-logic-tree';
import { EditorService } from '../../services/editor';
import { debounce } from '../../utils';

interface IProjectDataFormProps {
	project: EditorService;
	ready: boolean;
}

const ProjectDataForm: React.FC<IProjectDataFormProps> = ({
	project,
	ready,
}) => {
	const loading = !project || !ready;

	const [projectName, setProjectName] = useState(project?.logic?.name || '');
	const [description, setDescription] = useState(
		project?.logic?.description || '',
	);

	// Refs to store latest values
	const projectNameRef = useRef(projectName);
	const descriptionRef = useRef(description);

	useEffect(() => {
		projectNameRef.current = projectName;
	}, [projectName]);

	useEffect(() => {
		descriptionRef.current = description;
	}, [description]);

	// This function does the actual update.
	const updateProject = async (name: string, desc: string) => {
		let actionName = StateMutationAction.UpdateProjectMetadata;

		if (name !== project?.logic?.name && desc !== project?.logic?.description) {
			actionName = StateMutationAction.UpdateProjectMetadata;
		} else if (name !== project?.logic?.name) {
			actionName = StateMutationAction.RenameProject;
		} else if (desc !== project?.logic?.description) {
			actionName = StateMutationAction.ChangeProjectDescription;
		}

		const changeSet = project?.logic?.addChangeSet(
			new ChangeSet(
				project?.logic,
				ProjectState.sessionAuthor,
				new Date().toISOString(),
				project?.logic,
				false,
				actionName,
			),
		);
		project?.logic.metaSync({ name, description: desc }, changeSet);
		await changeSet?.closeAsync({
			awaitExternalPersist: false,
			persistType: 'parallel',
		});
	};

	// Use useRef to ensure the debounced function is created only once.
	const debouncedUpdateRef = useRef(
		debounce(() => {
			updateProject(projectNameRef.current, descriptionRef.current);
		}, 600),
	);

	const handleNameChange = (value: string) => {
		setProjectName(value);
		debouncedUpdateRef.current();
	};

	const handleDescriptionChange = (value: string) => {
		setDescription(value);
		debouncedUpdateRef.current();
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (loading) return;

		updateProject(projectName, description);
	};

	return (
		<form
			style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
			onSubmit={handleSubmit}
		>
			<TextField
				name='name'
				label='Project name'
				value={projectName}
				onChange={handleNameChange}
				disabled={loading}
			/>
			<TextArea
				name='description'
				label='Description'
				variant='filled'
				inputProps={{
					className: 'project-description-field',
				}}
				value={description}
				onChange={handleDescriptionChange}
				disabled={loading}
			/>
		</form>
	);
};

export default React.memo(ProjectDataForm);
